import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import {
  CircularProgress,
  TextField,
  Button,
  Paper,
  TablePagination
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import styled from 'styled-components';
import AgGrid from '@components/AgGrid/AgGrid';
import { fetchElectronicDeliveryRecords } from '@modules/appraisalAdmin/actions/notice';

const ElectronicDeliveryDetails = () => {
  const { noticeId } = useParams();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const columns = [
    {
      headerName: 'PID',
      field: 'pID',
      cellRenderer: ({ value, data }) => (
        <a href={`/appraisal/property/${value}/${data.pYear}/0/0`} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      )
    },
    {
      headerName: 'Request ID',
      field: 'requestID',
      cellRenderer: ({ value, data }) => {
        const url = data.requestType === 'Agent'
          ? `/appraisal/electronicDeliveryRequestAgent/${value}`
          : `/appraisal/electronicDeliveryRequestTaxpayer/${value}`;
        
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        );
      }
    },
    {
      headerName: 'Request Type',
      field: 'requestType'
    },
    {
      headerName: 'Email Address',
      field: 'emailAddress'
    },
    {
      headerName: 'Year',
      field: 'pYear'
    }
  ];

  const fetchRecords = useCallback(async (page = 1, size = 10, email = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(size),
        ...(email && { emailAddress: email })
      }).toString();

      const response = await dispatch(fetchElectronicDeliveryRecords({
        noticeId,
        params
      }));
      
      setData(response.results || []);
      setTotalRecords(response.recordCount || 0);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, noticeId]);

  useEffect(() => {
    fetchRecords(currentPage, pageSize);
  }, [currentPage, pageSize, fetchRecords]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchRecords(1, pageSize, emailFilter);
  };

  const handleClear = () => {
    setEmailFilter('');
    setCurrentPage(1);
    fetchRecords(1, pageSize);
  };

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <Container>
      <Paper>
        <HeaderContainer>
          <BreadcrumbContainer>
            <Link to="/appraisal-process-manager/appraisal-notices">
              Appraisal Notice
            </Link>
            {` > ${noticeId} > Electronic Delivery`}
          </BreadcrumbContainer>

          <SearchContainer>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search by Email Address"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
          </SearchContainer>
        </HeaderContainer>

        <TableContainer className="ag-theme-material">
          {loading ? (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          ) : (
            <AgGrid
              columnDefs={columns}
              rowData={data}
              suppressPagination
              rowModelType="clientSide"
              suppressCellSelection
              onGridReady={onGridReady}
            />
          )}
        </TableContainer>
      </Paper>
      {!loading && (
        <TablePagination
          component="div"
          count={totalRecords}
          page={currentPage - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px 20px 0;
  
  .MuiPaper-root {
    height: calc(100vh - 140px); // minus header and pagination
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const HeaderContainer = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`;

const BreadcrumbContainer = styled.div`
  font-size: 14px;
  
  a {
    color: #1976d2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  .MuiTextField-root {
    width: 300px;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;

  .ag-theme-material {
    height: 100%;
    width: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export default ElectronicDeliveryDetails; 